import { supabase } from '@/integrations/supabase/client';
import { User, UserRole } from './types';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/services/logger';
import { withCaptcha } from '@/lib/captcha';

type AppRole = Database['public']['Enums']['app_role'];

/**
 * Snapshot localStorage du dernier profil utilisateur fetch avec succès.
 *
 * Utilisé pour rendre une UX dégradée gracieuse quand `fetchUserProfile` :
 *   - timeout (réseau lent / 5G→Wifi handover en cours)
 *   - erreur transient (Supabase 503 / DNS hiccup)
 *
 * Sans ce snapshot, le `buildUserFromJwtClaims` fallback ne contient que les
 * claims JWT (email, role, organizationId) et perd `avatar` → l'utilisateur
 * voit ses initiales "JK" au lieu de son avatar choisi.
 *
 * Le snapshot est par-userId pour ne pas leaker entre comptes sur un même
 * device (kiosque, démo) et expire à 30j pour les anciens comptes.
 *
 * Format : { savedAt: number, user: User } par userId.
 */
const USER_SNAPSHOT_KEY = 'koken_user_snapshot_v1';
const USER_SNAPSHOT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

interface UserSnapshotEntry {
  savedAt: number;
  user: User;
}

function readUserSnapshot(userId: string): User | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_SNAPSHOT_KEY) : null;
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, UserSnapshotEntry>;
    const entry = map[userId];
    if (!entry) return null;
    if (Date.now() - entry.savedAt > USER_SNAPSHOT_TTL_MS) return null;
    return entry.user;
  } catch {
    return null;
  }
}

function writeUserSnapshot(user: User): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(USER_SNAPSHOT_KEY);
    const map: Record<string, UserSnapshotEntry> = raw ? JSON.parse(raw) : {};
    map[user.id] = { savedAt: Date.now(), user };
    localStorage.setItem(USER_SNAPSHOT_KEY, JSON.stringify(map));
  } catch {
    // localStorage plein / privé — pas grave, on continue
  }
}

// Mapper les rôles Supabase vers les types User
const mapSupabaseRoleToUserRole = (role: AppRole): UserRole => {
  const roleMap: Partial<Record<AppRole, UserRole>> = {
    'gamepass-admin': 'gamepass-admin',
    'super-manager': 'super-manager',
    'super-cse': 'super-cse',
    'super-rse': 'super-rse',
    'manager': 'manager',
    'cse-member': 'cse-member',
    'rse-manager': 'rse-manager',
    'employee': 'employee',
  };
  return roleMap[role] || 'employee';
};

/**
 * Build minimal user from JWT claims (fallback when profile fetch fails).
 *
 * Stratégie de récupération avatar (3 niveaux) :
 *   1. snapshot localStorage du dernier fetch réussi (28.5% des sessions le
 *      voient car le user a déjà chargé une fois aujourd'hui)
 *   2. `userMetadata.avatar_url` ou `userMetadata.avatar` (souvent set par
 *      Supabase à l'inscription ou par un trigger DB qui sync user_profiles
 *      → auth.users.user_metadata)
 *   3. undefined → HeroRingSignature affichera les initiales (régression
 *      acceptable seulement pour première session offline jamais loggée
 *      avant — < 1% des cas)
 *
 * Fix 2026-05-28 : sans ce snapshot, le bug "JK" (initiales) frappait tous
 * les users qui voyaient un timeout fetch profile (5G→Wifi handover, lag
 * Supabase). 82.5% des users ont un avatar DiceBear externe non-cacheable
 * par défaut, donc le fallback initials était quasi-systématique en
 * dégradation réseau.
 */
const buildUserFromJwtClaims = async (userId: string): Promise<User | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const { user: authUser } = session;
    const appMetadata = authUser.app_metadata || {};
    const userMetadata = authUser.user_metadata || {};

    logger.debug('[JWT FALLBACK] Building user from JWT claims');

    // Codex Review P1 fix 2026-05-28 — Ne JAMAIS restaurer les fields auth-critiques
    // (role, status, organizationId, departmentId, teamId, managerId) depuis un
    // snapshot 30j. Ces fields peuvent avoir changé côté admin DB : un user
    // désactivé / dégradé / changé d'org garderait des privilèges périmés si on
    // restaurait le snapshot wholesale.
    //
    // On garde uniquement les fields display-only du snapshot (avatar, name,
    // nickname, email, company...) qui n'impactent pas les guards d'accès.
    // Les fields auth viennent TOUJOURS du JWT actuel (attesté par le serveur
    // courant lors du token refresh ; un user désactivé verrait son token
    // rejeté au prochain auth.getUser()).
    const snapshot = readUserSnapshot(userId);

    const avatarFromMetadata = userMetadata.avatar_url
      || userMetadata.avatar
      || undefined;

    const minimalUser: User = {
      // === Fields auth-critiques : JWT actuel uniquement ====================
      id: userId,
      email: authUser.email || '',
      role: mapSupabaseRoleToUserRole(appMetadata.primary_role as AppRole || 'employee'),
      status: 'validated', // forcé validated car le token est encore valide côté serveur
      organizationId: appMetadata.organization_id || userMetadata.organizationId || undefined,
      // === Fields display-only : snapshot localStorage en priorité, JWT en fallback ==
      // Si l'avatar a été uploadé après le dernier snapshot, on perd jusqu'à 30j de
      // freshness mais on évite le fallback "JK" initials (le tradeoff acceptable
      // pour résilience offline).
      avatar: snapshot?.avatar ?? avatarFromMetadata,
      name: snapshot?.name ?? userMetadata.full_name ?? authUser.email?.split('@')[0] ?? 'User',
      nickname: snapshot?.nickname ?? userMetadata.nickname ?? authUser.email?.split('@')[0] ?? 'User',
      company: snapshot?.company,
      organizationName: snapshot?.organizationName,
      department: snapshot?.department,
      team: snapshot?.team,
      // Hierarchy fields : JWT-only car ils impactent les guards manager/team
      departmentId: undefined,
      teamId: undefined,
      managerId: undefined,
    };

    logger.debug(
      `[JWT FALLBACK] Minimal user built: ${minimalUser.email}, role: ${minimalUser.role}, ` +
      `avatar source: ${snapshot?.avatar ? 'snapshot' : (avatarFromMetadata ? 'metadata' : 'none')}`,
    );
    return minimalUser;
  } catch (error) {
    logger.error('[JWT FALLBACK] Failed to build user from JWT:', error);
    return null;
  }
};

export const supabaseAuthService = {
  /**
   * ✅ SIMPLIFIÉ : Appel Supabase direct avec timeout 5s + fallback JWT
   * Plus de couche cache intermédiaire
   */
  fetchUserProfile: async (userId: string): Promise<User | null> => {
    logger.info('[AUTH SERVICE] fetchUserProfile direct - userId:', userId);

    try {
      // Timeout 5s — compromis entre UX réactive et tolérance handover réseau.
      // Fix 2026-05-28 : avant à 3s, trop court pour les transitions 5G→Wifi
      // (cellulaire iOS qui re-établit la connexion en 2-4s) → trigger fallback
      // JWT trop souvent → bug avatar "JK" en cascade. 5s couvre 95% des cas
      // réels sans paraître bloquant à l'utilisateur (loader visible 1-2s
      // max en pratique car le fetch réussit largement avant le timeout).
      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => {
          logger.info('[AUTH SERVICE] Timeout 5s atteint');
          resolve(null);
        }, 5000)
      );

      const fetchPromise = (async () => {
        // 1+2. Profile + roles en PARALLÈLE (Promise.all)
        //   - user_profiles : autorité du profil (statut, avatar, org, etc.)
        //   - user_roles    : liste des rôles → on prend le plus prioritaire
        // Avant : queries en série (~150-300ms cumulés). Après : ~max(p1,p2).
        // Les deux queries sont 100% indépendantes (pas de dépendance de
        // données entre elles), donc Promise.all est safe.
        const [profileResult, rolesResult] = await Promise.all([
          supabase
            .from('user_profiles')
            .select(`
              *,
              organizations:organization_id(name),
              departments:department_id(name),
              teams:team_id(name)
            `)
            .eq('id', userId)
            .single(),
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId),
        ]);

        const { data: profile, error: profileError } = profileResult;
        const { data: roles } = rolesResult;

        if (profileError || !profile) {
          logger.error('[AUTH SERVICE] Profile error:', profileError?.message);
          return null;
        }

        const ROLE_PRIORITY: Record<string, number> = {
          'gamepass-admin': 100,
          'super-manager': 80,
          'manager': 60,
          'employee': 20,
        };

        const sortedRoles = (roles || []).sort(
          (a, b) => (ROLE_PRIORITY[b.role] || 0) - (ROLE_PRIORITY[a.role] || 0)
        );

        const role = mapSupabaseRoleToUserRole((sortedRoles?.[0]?.role as AppRole) || 'employee');

        // 3. Construire l'objet User
        const user: User = {
          id: profile.id,
          email: profile.email,
          name: profile.full_name || profile.email?.split('@')[0] || 'User',
          nickname: profile.nickname || profile.email?.split('@')[0] || 'User',
          role,
          userType: (profile.user_type as 'internal' | 'client') || 'client',
          status: (profile.status as 'pending' | 'validated' | 'rejected') || 'validated',
          avatar: profile.avatar_url || undefined,
          organizationId: profile.organization_id || undefined,
          departmentId: profile.department_id || undefined,
          teamId: profile.team_id || undefined,
          managerId: profile.manager_id || undefined,
          company: (profile.organizations as any)?.name,
          organizationName: (profile.organizations as any)?.name,
          department: (profile.departments as any)?.name,
          team: (profile.teams as any)?.name,
        };

        return user;
      })();

      // Race entre fetch et timeout
      const result = await Promise.race([fetchPromise, timeoutPromise]);

      if (result) {
        logger.info('[AUTH SERVICE] Profile fetched:', result.email);
        // ✅ Snapshot localStorage du profil pour résilience offline (fix avatar "JK"
        //    quand le prochain fetch timeout en handover 5G→Wifi). TTL 30j.
        writeUserSnapshot(result);
        return result;
      }

      // Timeout ou erreur → fallback JWT (qui lit le snapshot localStorage en
      // priorité s'il existe, sinon assemble depuis JWT claims + user_metadata).
      logger.warn('[AUTH SERVICE] Timeout/error, using JWT fallback');
      return buildUserFromJwtClaims(userId);

    } catch (error) {
      logger.error('[AUTH SERVICE] Error:', error);
      return buildUserFromJwtClaims(userId);
    }
  },

  /**
   * Connexion avec email/password
   */
  login: async (email: string, password: string, captchaToken?: string | null): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: withCaptcha(undefined, captchaToken),
    });

    if (error) throw error;
  },

  /**
   * Déconnexion
   */
  logout: async (): Promise<void> => {
    // Purge le snapshot localStorage AVANT signOut pour qu'un user changeant
    // de compte ne voie pas brièvement l'avatar/nom du compte précédent
    // restauré depuis le snapshot.
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(USER_SNAPSHOT_KEY);
      }
    } catch { /* private mode / quota */ }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Récupérer l'utilisateur courant depuis la session
   */
  getCurrentUser: async (): Promise<User | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) return null;

    return supabaseAuthService.fetchUserProfile(session.user.id);
  },

  /**
   * Mettre à jour le nickname
   */
  updateNickname: async (userId: string, nickname: string): Promise<User> => {
    const { error } = await supabase
      .from('user_profiles')
      .update({ nickname, updated_at: new Date().toISOString() })
      .eq('id', userId);

    if (error) throw error;

    const updatedUser = await supabaseAuthService.fetchUserProfile(userId);
    if (!updatedUser) throw new Error('Failed to fetch updated user profile');

    return updatedUser;
  },

  /**
   * Mettre à jour le profil utilisateur
   */
  updateProfile: async (
    userId: string,
    updates: { name?: string; avatar?: string | null; department?: string }
  ): Promise<User> => {
    const profileUpdates: any = { updated_at: new Date().toISOString() };

    if (updates.name !== undefined) {
      profileUpdates.full_name = updates.name;
    }
    if (updates.avatar !== undefined) {
      profileUpdates.avatar_url = updates.avatar;
    }

    // `.select('id')` est indispensable : un PATCH PostgREST qui ne matche
    // AUCUNE ligne (RLS refusant l'accès, `auth.uid()` nul sur une session
    // expirée) répond 204 sans erreur. Sans ce select on lisait « succès »
    // alors que rien n'avait été écrit — l'avatar semblait accepté puis
    // revenait à l'ancien au rechargement.
    const { data: updatedRows, error } = await supabase
      .from('user_profiles')
      .update(profileUpdates)
      .eq('id', userId)
      .select('id');

    if (error) throw error;

    if (!updatedRows || updatedRows.length === 0) {
      logger.error('[AUTH SERVICE] updateProfile a touché 0 ligne', { userId });
      throw new Error(
        "Profil non mis à jour : aucune ligne modifiée (session expirée ou droits insuffisants)."
      );
    }

    // L'écriture est confirmée à ce stade. Si la relecture échoue (réseau qui
    // coupe juste après), c'est un problème d'affichage, pas d'enregistrement :
    // on ne doit PAS le remonter comme un échec de sauvegarde à l'utilisateur.
    const updatedUser = await supabaseAuthService.fetchUserProfile(userId);
    if (!updatedUser) {
      logger.warn('[AUTH SERVICE] Profil enregistré mais relecture impossible', { userId });
      throw new Error('PROFILE_SAVED_REFETCH_FAILED');
    }

    return updatedUser;
  },

  /**
   * Récupérer les employés d'un manager
   */
  getEmployeesByManager: async (managerId: string): Promise<User[]> => {
    const { data: profiles, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('manager_id', managerId);

    if (error) {
      logger.error('[AUTH SERVICE] Error fetching employees:', error);
      return [];
    }

    if (!profiles) return [];

    // Récupérer les rôles et infos enrichies pour chaque utilisateur
    const usersWithRoles = await Promise.all(
      profiles.map(async (profile) => {
        // Rôles
        const { data: rolesData } = await supabase
          .rpc('get_user_roles', { _user_id: profile.id });

        const roles = (rolesData?.map((r: any) => r.role) || []) as AppRole[];
        const primaryRole = roles[0] || 'employee';

        // Organisation
        let organizationName: string | undefined;
        if (profile.organization_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', profile.organization_id)
            .maybeSingle();
          organizationName = org?.name;
        }

        // Département
        let departmentName: string | undefined;
        if (profile.department_id) {
          const { data: dept } = await supabase
            .from('departments')
            .select('name')
            .eq('id', profile.department_id)
            .single();
          departmentName = dept?.name;
        }

        // Équipe
        let teamName: string | undefined;
        if (profile.team_id) {
          const { data: team } = await supabase
            .from('teams')
            .select('name')
            .eq('id', profile.team_id)
            .single();
          teamName = team?.name;
        }

        const user: User = {
          id: profile.id,
          email: profile.email,
          name: profile.full_name || profile.email.split('@')[0],
          nickname: profile.nickname || profile.email.split('@')[0],
          role: mapSupabaseRoleToUserRole(primaryRole),
          userType: (profile.user_type as 'internal' | 'client') || 'client',
          status: (profile.status as 'pending' | 'validated' | 'rejected') || 'validated',
          avatar: profile.avatar_url || undefined,
          managerId: profile.manager_id || undefined,
          organizationId: profile.organization_id || undefined,
          departmentId: profile.department_id || undefined,
          teamId: profile.team_id || undefined,
           department: departmentName,
           team: teamName,
           company: organizationName,
           organizationName: organizationName,
        };

        return user;
      })
    );

    return usersWithRoles;
  },
};
